<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Tarifario</h1>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">

          <!-- Toggle de dirección -->
          <div class="d-flex mb-2 mb-md-0">
            <button
              type="button"
              :class="['button', routeType === 'district_to_airport' ? 'button-primary' : 'button-light']"
              @click="setRouteType('district_to_airport')"
            >
              Distrito → Aeropuerto
            </button>
            <button
              type="button"
              :class="['button ml-2', routeType === 'airport_to_district' ? 'button-primary' : 'button-light']"
              @click="setRouteType('airport_to_district')"
            >
              Aeropuerto → Distrito
            </button>
          </div>

          <!-- Buscador -->
          <div class="d-flex align-items-center">
            <div class="d-none d-md-flex">
              <button type="button" class="button-tooltip text-dark font-weight-bold" v-b-tooltip.hover title="Recargar"
                @click="loadData()">
                <i class="ri-loop-right-line"></i>
              </button>
            </div>
            <SearchBar placeholder="Buscar distrito..." @search="searchText = $event" @input="searchText = $event" class="ml-md-2" />
          </div>
        </div>
      </div>

      <div class="mb-0" style="overflow-x: auto;">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th rowspan="2" class="th-distrito align-middle">Distrito</th>
              <th colspan="4" class="text-center th-base-header">Costo Base</th>
              <th
                v-for="pt in priceTypeColumns"
                :key="`grp-${pt.code}`"
                colspan="4"
                class="text-center"
                :style="getPtHeaderStyle(pt.index)"
              >{{ pt.name }}</th>
            </tr>
            <tr>
              <th v-for="vt in vehicleTypes" :key="`base-${vt}`" class="text-center th-base-sub">{{ vt }}</th>
              <template v-for="pt in priceTypeColumns">
                <th
                  v-for="vt in vehicleTypes"
                  :key="`${pt.code}-${vt}`"
                  class="text-center"
                  :style="getPtSubStyle(pt.index)"
                >{{ vt }}</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredRows.length === 0">
              <td :colspan="1 + 4 + priceTypeColumns.length * 4" class="text-center">No se encontraron distritos</td>
            </tr>
            <tr v-for="row in filteredRows" :key="row.id">
              <td class="font-weight-bold">{{ row.name }}</td>
              <td v-for="vt in vehicleTypes" :key="`base-${vt}`" class="text-center td-base">{{ getVal(row, vt, 'base') }}</td>
              <template v-for="pt in priceTypeColumns">
                <td
                  v-for="vt in vehicleTypes"
                  :key="`${pt.code}-${vt}`"
                  class="text-center"
                >{{ getVal(row, vt, pt.code) }}</td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BasePriceService } from '@/core/services'
import { Alert, SearchBar } from 'wize-admin'

const PRIORITY = ['Miraflores', 'Barranco', 'San Isidro', 'Santiago de Surco', 'La Molina']
const VEHICLE_TYPES = ['Sedan', 'SUV', 'VAN', 'Sprinter']
const PROTECTED_ORDER = ['publico', 'qore', 'cliente_ff']
const PT_PALETTE = [
  {
    header: { backgroundColor: '#e3f2fd', color: '#1565c0', borderBottom: '2px solid #90caf9' },
    sub:    { backgroundColor: '#f0f7ff', color: '#1976d2' },
  },
  {
    header: { backgroundColor: '#f3e5f5', color: '#6a1b9a', borderBottom: '2px solid #ce93d8' },
    sub:    { backgroundColor: '#faf0fc', color: '#7b1fa2' },
  },
  {
    header: { backgroundColor: '#fff3e0', color: '#e65100', borderBottom: '2px solid #ffcc80' },
    sub:    { backgroundColor: '#fffaf0', color: '#f57c00' },
  },
  {
    header: { backgroundColor: '#e0f7fa', color: '#006064', borderBottom: '2px solid #80deea' },
    sub:    { backgroundColor: '#f0fdff', color: '#00838f' },
  },
  {
    header: { backgroundColor: '#f9fbe7', color: '#558b2f', borderBottom: '2px solid #c5e1a5' },
    sub:    { backgroundColor: '#fafff0', color: '#689f38' },
  },
]

export default {
  components: { SearchBar },
  data() {
    return {
      vehicleTypes: VEHICLE_TYPES,
      routeType: 'district_to_airport',
      searchText: '',
      rows: [],
    }
  },
  computed: {
    priceTypeColumns() {
      if (!this.rows.length) return []
      const seen = new Set()
      const pts = []
      this.rows.forEach(r => {
        const prices = r.prices || []
        prices.forEach(p => {
          if (!seen.has(p.price_type_code)) {
            seen.add(p.price_type_code)
            pts.push({ code: p.price_type_code, name: p.price_type_name })
          }
        })
      })
      pts.sort((a, b) => {
        const ai = PROTECTED_ORDER.indexOf(a.code)
        const bi = PROTECTED_ORDER.indexOf(b.code)
        if (ai >= 0 && bi >= 0) return ai - bi
        if (ai >= 0) return -1
        if (bi >= 0) return 1
        return a.name.localeCompare(b.name)
      })
      return pts.map((pt, i) => ({ ...pt, index: i }))
    },
    districtMap() {
      const map = {}
      this.rows.forEach(r => {
        if (!map[r.district_id]) {
          map[r.district_id] = { id: r.district_id, name: r.district_name, vehicles: {} }
        }
        const vData = { base: r.base_amount }
        const rowPrices = r.prices || []
        rowPrices.forEach(p => { vData[p.price_type_code] = p.calculated_amount })
        map[r.district_id].vehicles[r.vehicle_type] = vData
      })
      return map
    },
    filteredRows() {
      const q = this.searchText.trim().toLowerCase()

      const hasPrices = (id) => {
        const d = this.districtMap[id]
        return d && Object.values(d.vehicles).some(v => (v.base ?? 0) > 0)
      }

      const priorityIndex = (name) => {
        const i = PRIORITY.indexOf(name)
        return i >= 0 ? i : Infinity
      }

      return Object.values(this.districtMap)
        .filter(d => !q || d.name.toLowerCase().includes(q))
        .sort((a, b) => {
          const pa = priorityIndex(a.name)
          const pb = priorityIndex(b.name)
          if (pa !== Infinity && pb !== Infinity) return pa - pb
          if (pa !== Infinity) return -1
          if (pb !== Infinity) return 1
          const ha = hasPrices(a.id)
          const hb = hasPrices(b.id)
          if (ha && !hb) return -1
          if (!ha && hb) return 1
          return a.name.localeCompare(b.name)
        })
    },
  },
  methods: {
    getPtHeaderStyle(index) {
      const c = PT_PALETTE[index % PT_PALETTE.length]
      return c ? c.header : {}
    },
    getPtSubStyle(index) {
      const c = PT_PALETTE[index % PT_PALETTE.length]
      return c ? c.sub : {}
    },
    getVal(row, vehicleType, priceCode) {
      const v = row.vehicles[vehicleType]
      if (!v) return '—'
      const amount = priceCode === 'base' ? v.base : v[priceCode]
      return amount != null ? `S/ ${parseFloat(amount).toFixed(2)}` : '—'
    },
    setRouteType(rt) {
      if (rt === this.routeType) return
      this.routeType = rt
      this.loadData()
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await BasePriceService.getCalculatedPrices(this.routeType)
        this.rows = res.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e?.errors?.message || 'Error al cargar los precios calculados')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async mounted() {
    await this.loadData()
  },
}
</script>

<style lang="stylus" scoped>
.th-distrito
  min-width 140px

.th-base-header
  background-color #e8f5e9
  color #2e7d32
  border-bottom 2px solid #a5d6a7

.th-base-sub
  background-color #f1f8f2
  color #388e3c
  font-weight normal
  min-width 90px

.td-base
  color #2e7d32
  font-weight 500
</style>
